/**
 * App icon set — Hugeicons (MIT, https://hugeicons.com).
 *
 * Every screen imports icons from *this* module rather than from the vendor
 * package directly, so the product speaks one icon language: one library, one
 * weight, one optical size. That seam is what made the move off Phosphor a
 * single-file change rather than a grep across twenty pages.
 *
 * Why Hugeicons — the candidates were rendered side by side at the sizes they
 * actually ship at, inside the real components, rather than judged from a
 * gallery. Phosphor at `regular` is wiry: at 16px in a list row the strokes
 * thin out and the glyph reads as traced rather than drawn. Hugeicons has
 * rounded terminals and a heavier, more even stroke, which is the construction
 * SF Symbols uses, and it holds its weight next to 13–14px text.
 *
 * Conventions:
 *   • stroke 2 everywhere. Heavier only where a hairline would disappear.
 *   • 16px inside dense UI (labels, list rows), 18–20px for standalone actions,
 *     32–40px for empty states.
 *   • Icons stay recessive and inherit ink from `currentColor`. The one place
 *     they carry colour is the dashboard activity feed, where the tint encodes
 *     delivery outcome and is therefore information, not decoration.
 *
 * Four names were changed after looking at them rather than after reading the
 * export list: `RadioIcon` draws a set-top box, not a broadcast; `AiChat01Icon`
 * is a gear in a bubble; `ChartBarLineIcon` reads as a building; and
 * `File01Icon` was standing in for both Document and Content Library, so the
 * two nav entries rendered identically.
 */
import { HugeiconsIcon } from '@hugeicons/react';
import {
  Activity01Icon,
  AiMagicIcon,
  Alert01Icon,
  Alert02Icon,
  Analytics01Icon,
  ArrowDown01Icon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  ArrowTurnBackwardIcon,
  ArrowUp01Icon,
  ArrowUpRight01Icon,
  AudioWave01Icon,
  Book01Icon,
  BubbleChatIcon,
  Calendar01Icon,
  Calendar03Icon,
  CallEnd01Icon,
  CallIcon,
  CallOutgoing01Icon,
  Cancel01Icon,
  CancelCircleIcon,
  ChartBarBigIcon,
  ChartLineData01Icon,
  ChatBotIcon,
  CheckmarkCircle02Icon,
  CheckmarkSquare01Icon,
  CircleGaugeIcon,
  Clock01Icon,
  Delete02Icon,
  Download01Icon,
  Edit02Icon,
  File01Icon,
  FlashIcon,
  FloppyDiskIcon,
  Home01Icon,
  InformationCircleIcon,
  Layers01Icon,
  LeftToRightListBulletIcon,
  Loading03Icon,
  Logout01Icon,
  Mail01Icon,
  PanelLeftCloseIcon,
  PanelLeftOpenIcon,
  Megaphone01Icon,
  Menu01Icon,
  Message01Icon,
  Message02Icon,
  Mic01Icon,
  MinusSignIcon,
  PauseIcon,
  PlayIcon,
  PlusSignIcon,
  RefreshIcon,
  Search01Icon,
  SecurityCheckIcon,
  SentIcon,
  SquareIcon,
  SquareLock01Icon,
  StarIcon,
  StethoscopeIcon,
  TaskDone01Icon,
  Tick02Icon,
  Upload01Icon,
  UserAdd01Icon,
  UserGroupIcon,
  UserIcon,
  ViewIcon,
  ViewOffSlashIcon,
  WhatsappIcon,
} from '@hugeicons/core-free-icons';

/**
 * Wrap a Hugeicons glyph so it takes the props the rest of the app already
 * passes: `size`, `className`, and colour inherited from `currentColor`.
 *
 * `weight` is translated rather than ignored. The screens were written against
 * Phosphor's vocabulary and five call sites still ask for `fill` (star
 * ratings) or `bold` (an 11px caret). Hugeicons has no weight axis — its
 * glyphs are strokes — but the paths are closed, so filling one yields a solid
 * glyph, which is exactly what a filled star is; this was checked by rendering
 * it, not assumed. Translating here keeps the swap inside this file instead of
 * turning it into an edit across five pages.
 */
const hugeicon = (icon, displayName) => {
  // `brand` is swallowed, not forwarded. Call sites treat icons
  // polymorphically -- the activity feed renders whichever icon a row maps to
  // and passes brand={false} to make a mark inherit the tile's ink. On a
  // brand mark that is meaningful; on one of these it is meaningless, and
  // spreading it onto the <svg> put brand="false" in the DOM and a React
  // warning in every console.
  const Component = ({ size = 18, strokeWidth, weight, className, brand: _brand, ...props }) => {
    // Spread `fill` in only when a filled glyph is actually wanted. Passing
    // `fill={undefined}` is not the same as leaving the prop off: it suppresses
    // the vendor component's own default, and an <svg> with no fill falls back
    // to the SVG initial value, which is solid black. Every closed path then
    // floods — the whole sidebar rendered as black blobs.
    const filled = weight === 'fill' ? { fill: 'currentColor' } : null;
    return (
      <HugeiconsIcon
        icon={icon}
        size={size}
        strokeWidth={strokeWidth ?? (weight === 'bold' ? 2.5 : 2)}
        className={className}
        {...filled}
        {...props}
      />
    );
  };
  Component.displayName = displayName;
  return Component;
};

/* ── Domain ─────────────────────────────────────────────────────────────── */
export const IconPatients = hugeicon(UserGroupIcon, 'IconPatients');
export const IconPatientNew = hugeicon(UserAdd01Icon, 'IconPatientNew');
export const IconDiagnosis = hugeicon(StethoscopeIcon, 'IconDiagnosis');
export const IconSMS = hugeicon(Message02Icon, 'IconSMS');
export const IconSmartSMS = hugeicon(AiMagicIcon, 'IconSmartSMS');
export const IconVoiceSMS = hugeicon(Megaphone01Icon, 'IconVoiceSMS');
export const IconVoiceCall = hugeicon(CallIcon, 'IconVoiceCall');
export const IconCallAnswered = hugeicon(CallIcon, 'IconCallAnswered');
export const IconCallPlaced = hugeicon(CallOutgoing01Icon, 'IconCallPlaced');
export const IconCallMissed = hugeicon(CallEnd01Icon, 'IconCallMissed');
export const IconAIVoice = hugeicon(AudioWave01Icon, 'IconAIVoice');
export const IconChatbot = hugeicon(ChatBotIcon, 'IconChatbot');
/**
 * Monochrome WhatsApp glyph, for toolbars and labels where the mark has to
 * sit at the same ink weight as its neighbours. Where the point is to show
 * the channel a message actually travelled down, use IconBrandWhatsApp,
 * which is the official mark in the official green.
 */
export const IconWhatsApp = hugeicon(WhatsappIcon, 'IconWhatsApp');

/* ── Metrics ────────────────────────────────────────────────────────────── */
export const IconAnswerRate = hugeicon(CircleGaugeIcon, 'IconAnswerRate');
export const IconTrendLine = hugeicon(ChartLineData01Icon, 'IconTrendLine');
export const IconChart = hugeicon(ChartBarBigIcon, 'IconChart');
export const IconActivity = hugeicon(Activity01Icon, 'IconActivity');

/* ── Actions & affordances ──────────────────────────────────────────────── */
export const IconRefresh = hugeicon(RefreshIcon, 'IconRefresh');
export const IconArrow = hugeicon(ArrowRight01Icon, 'IconArrow');
export const IconExternal = hugeicon(ArrowUpRight01Icon, 'IconExternal');
export const IconDeltaUp = hugeicon(ArrowUp01Icon, 'IconDeltaUp');
export const IconDeltaDown = hugeicon(ArrowDown01Icon, 'IconDeltaDown');
export const IconDeltaFlat = hugeicon(MinusSignIcon, 'IconDeltaFlat');
export const IconSpinner = hugeicon(Loading03Icon, 'IconSpinner');
export const IconMenu = hugeicon(Menu01Icon, 'IconMenu');
export const IconClose = hugeicon(Cancel01Icon, 'IconClose');
export const IconSignOut = hugeicon(Logout01Icon, 'IconSignOut');

/* ── Status ─────────────────────────────────────────────────────────────── */
export const IconSuccess = hugeicon(CheckmarkCircle02Icon, 'IconSuccess');
export const IconPending = hugeicon(Clock01Icon, 'IconPending');
export const IconWarning = hugeicon(Alert01Icon, 'IconWarning');
export const IconError = hugeicon(Alert02Icon, 'IconError');
export const IconInfo = hugeicon(InformationCircleIcon, 'IconInfo');
export const IconStar = hugeicon(StarIcon, 'IconStar');
export const IconFailed = hugeicon(CancelCircleIcon, 'IconFailed');

/* ── Editing & data ─────────────────────────────────────────────────────── */
export const IconEdit = hugeicon(Edit02Icon, 'IconEdit');
export const IconDelete = hugeicon(Delete02Icon, 'IconDelete');
export const IconAdd = hugeicon(PlusSignIcon, 'IconAdd');
export const IconSave = hugeicon(FloppyDiskIcon, 'IconSave');
export const IconUpload = hugeicon(Upload01Icon, 'IconUpload');
export const IconDownload = hugeicon(Download01Icon, 'IconDownload');
export const IconSearch = hugeicon(Search01Icon, 'IconSearch');
export const IconSend = hugeicon(SentIcon, 'IconSend');
export const IconUndo = hugeicon(ArrowTurnBackwardIcon, 'IconUndo');
export const IconDocument = hugeicon(File01Icon, 'IconDocument');
export const IconList = hugeicon(LeftToRightListBulletIcon, 'IconList');

/* ── Selection ──────────────────────────────────────────────────────────── */
export const IconCheckbox = hugeicon(SquareIcon, 'IconCheckbox');
export const IconCheckboxChecked = hugeicon(CheckmarkSquare01Icon, 'IconCheckboxChecked');
export const IconCheck = hugeicon(Tick02Icon, 'IconCheck');

/* ── Playback & scheduling ──────────────────────────────────────────────── */
export const IconPlay = hugeicon(PlayIcon, 'IconPlay');
export const IconPause = hugeicon(PauseIcon, 'IconPause');
export const IconCalendar = hugeicon(Calendar01Icon, 'IconCalendar');
export const IconSchedule = hugeicon(Calendar03Icon, 'IconSchedule');
export const IconInstant = hugeicon(FlashIcon, 'IconInstant');

/* ── Directional ────────────────────────────────────────────────────────── */
export const IconChevronLeft = hugeicon(ArrowLeft01Icon, 'IconChevronLeft');
export const IconChevronRight = hugeicon(ArrowRight01Icon, 'IconChevronRight');
export const IconChevronUp = hugeicon(ArrowUp01Icon, 'IconChevronUp');
export const IconChevronDown = hugeicon(ArrowDown01Icon, 'IconChevronDown');
/** Disclosure caret on collapsible sections; points down when closed. */
export const IconChevron = hugeicon(ArrowDown01Icon, 'IconChevron');

/* ── Messaging & identity ───────────────────────────────────────────────── */
export const IconMessage = hugeicon(Message01Icon, 'IconMessage');
export const IconConversation = hugeicon(BubbleChatIcon, 'IconConversation');
export const IconUser = hugeicon(UserIcon, 'IconUser');
export const IconMicrophone = hugeicon(Mic01Icon, 'IconMicrophone');

/* ── Trust ──────────────────────────────────────────────────────────────── */
export const IconShield = hugeicon(SecurityCheckIcon, 'IconShield');

/* ── Chrome ─────────────────────────────────────────────────────────────── */
export const IconSidebarCollapse = hugeicon(PanelLeftCloseIcon, 'IconSidebarCollapse');
export const IconSidebarExpand = hugeicon(PanelLeftOpenIcon, 'IconSidebarExpand');

/* ── Navigation ─────────────────────────────────────────────────────────── */
export const IconOverview = hugeicon(Home01Icon, 'IconOverview');
export const IconReviewQueue = hugeicon(TaskDone01Icon, 'IconReviewQueue');
export const IconContentLibrary = hugeicon(Book01Icon, 'IconContentLibrary');
export const IconPrograms = hugeicon(Calendar03Icon, 'IconPrograms');
export const IconChannels = hugeicon(Layers01Icon, 'IconChannels');
export const IconAnalytics = hugeicon(Analytics01Icon, 'IconAnalytics');

/* ── Auth glyphs ─────────────────────────────────────────────────────────────
 *
 * The signed-out surfaces were the first to move to Hugeicons, back when the
 * rest of the app was still Phosphor; they now share the same family as
 * everything else and these aliases simply keep the auth screens readable at
 * their call sites.
 *
 * Two traps found by looking rather than by reading the export list:
 * `ViewOffIcon` renders as a broken squiggle at small sizes, and
 * `EyeClosedIcon` is closed lashes, not a slash — `ViewOffSlashIcon` is the
 * one that reads as "hidden".
 */
export const IconEmail = hugeicon(Mail01Icon, 'IconEmail');
export const IconPassword = hugeicon(SquareLock01Icon, 'IconPassword');
export const IconAuthUser = hugeicon(UserIcon, 'IconAuthUser');
export const IconEye = hugeicon(ViewIcon, 'IconEye');
export const IconEyeOff = hugeicon(ViewOffSlashIcon, 'IconEyeOff');
export const IconAuthError = hugeicon(Alert02Icon, 'IconAuthError');
export const IconAuthSuccess = hugeicon(CheckmarkCircle02Icon, 'IconAuthSuccess');
export const IconAuthSpinner = hugeicon(Loading03Icon, 'IconAuthSpinner');

/* ── Channel marks (signed-out artwork) ──────────────────────────────────── */
export const IconChannelWhatsApp = hugeicon(WhatsappIcon, 'IconChannelWhatsApp');
export const IconChannelCall = hugeicon(CallIcon, 'IconChannelCall');
export const IconChannelSMS = hugeicon(Message01Icon, 'IconChannelSMS');
export const IconChannelAnalytics = hugeicon(Analytics01Icon, 'IconChannelAnalytics');
export const IconChannelPatients = hugeicon(UserGroupIcon, 'IconChannelPatients');


/* ── Brand marks (simple-icons) ──────────────────────────────────────────────
 *
 * Real logos, not lookalikes.
 *
 * Phosphor and Hugeicons each ship a "WhatsApp" glyph redrawn in their own
 * house style. That is the right call inside a monochrome toolbar, where a
 * foreign mark would break the row, and the wrong one in the activity feed,
 * where the whole point is to show which channel a message actually travelled
 * down. simple-icons carries the official path and the official brand hex, so
 * what renders is the real mark at the real colour rather than an approximation.
 *
 * Imported as named exports from the package root: the package is ESM, so the
 * production build tree-shakes it and only the marks named below reach the
 * bundle — not all 3,400 of them.
 */
import { siWhatsapp } from 'simple-icons';

/**
 * Wrap a simple-icons record so it takes the same props as every other icon
 * here: `size` and `className`.
 *
 * Brand colour is the default because that is the reason to reach for this
 * module at all, but `brand={false}` falls back to `currentColor` for the
 * places where the mark has to sit in a monochrome row and inherit its ink.
 */
const brandIcon = (icon, displayName) => {
  const Component = ({ size = 18, brand = true, className, ...props }) => (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill={brand ? `#${icon.hex}` : 'currentColor'}
      className={className}
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      <path d={icon.path} />
    </svg>
  );
  Component.displayName = displayName;
  /** Official brand hex, for tinting the surface a mark sits on. */
  Component.brandHex = `#${icon.hex}`;
  return Component;
};

export const IconBrandWhatsApp = brandIcon(siWhatsapp, 'IconBrandWhatsApp');
